import React, { useRef, useState, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import axios from "axios";
import Select from "react-select";
import Spinner from "../../Assets/auth_spinner.svg";
import { getDisplayValue } from "../../utils/displayValue";

const PinnedSection = () => {
  gsap.registerPlugin(ScrollTrigger);

  const pinnedSectionRef = useRef(null);
  const zoomImageRef = useRef(null);
  const calculatorRef = useRef(null);
  const bgOverlayRef = useRef(null);
  const formStepsRef = useRef([]);

  const [currentStep, setCurrentStep] = useState(0);
  const [calculatedData, setCalculatedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    country: "",
    programme: "",
    city: "",
    university: "",
    major: "",
  });

  // State for dynamic options
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);

  // Fetch initial countries on component mount
  useEffect(() => {
    fetchFilterData();
  }, []);

  // Fetch filter data from API
  const fetchFilterData = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/calculator/filter${
          queryString ? `?${queryString}` : ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!params.country) {
        setCountries(response.data.data.countries || []);
      } else if (!params.city) {
        setCities(response.data.data.cities || []);
      } else if (!params.universityName) {
        setUniversities(response.data.data.universities || []);
      } else {
        setMajors(response.data.data.majors || []);
      }
    } catch (error) {
      // console.error("Error fetching filter data:", error);
    }
  };

  // Handle form field changes
  const handleChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [name]: value }));

    switch (name) {
      case "country":
        setFormData((prev) => ({
          ...prev,
          programme: "",
          city: "",
          university: "",
          major: "",
        }));
        setCities([]);
        setUniversities([]);
        setMajors([]);
        if (value) {
          fetchFilterData({ country: value });
        }
        break;

      case "programme":
        setFormData((prev) => ({
          ...prev,
          city: "",
          university: "",
          major: "",
        }));
        if (value && formData.country) {
          fetchFilterData({
            country: formData.country,
            program: value,
          });
        }
        break;

      case "city":
        setFormData((prev) => ({
          ...prev,
          university: "",
          major: "",
        }));
        if (value) {
          fetchFilterData({
            country: formData.country,
            program: formData.programme,
            city: value,
          });
        }
        break;

      case "university":
        setFormData((prev) => ({
          ...prev,
          major: "",
        }));
        if (value) {
          fetchFilterData({
            country: formData.country,
            program: formData.programme,
            city: formData.city,
            universityName: value,
          });
        }
        break;

      default:
        break;
    }
  };

  const saveCalculation = async () => {
    try {
      // console.log("Saving calculation with data:", formData);
      setIsLoading(true);

      // Sending data in the body of a POST request
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/calculator/calculate`,
        {
          country: formData.country,
          city: formData.city,
          programme: formData.programme,
          university: formData.university,
          major: formData.major,
        }
      );

      if (response?.data?.success) {
        setCurrentStep(5); // Move to results
        setCalculatedData(response.data.data);
      }
    } catch (error) {
      // console.error("Error saving calculation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useGSAP(
    () => {
      gsap.to(bgOverlayRef.current, {
        backgroundColor: "rgb(29, 53, 87)",
        opacity: 1,
        scrollTrigger: {
          trigger: pinnedSectionRef.current,
          start: "top top",
          end: "+=100%",
          scrub: true,
        },
        ease: "none",
      });

      gsap.to(zoomImageRef.current, {
        scale: 7.2,
        opacity: 0,
        scrollTrigger: {
          trigger: pinnedSectionRef.current,
          start: "top top",
          end: "+=100%",
          scrub: true,
          pin: true,
        },
        ease: "none",
      });

      gsap.to(calculatorRef.current, {
        scale: 1.2,
        opacity: 1,
        scrollTrigger: {
          trigger: pinnedSectionRef.current,
          start: "top top",
          end: "+=90%",
          scrub: true,
        },
        ease: "power2.out",
      });
    },
    { scope: pinnedSectionRef }
  );

  const nextStep = () => {
    if (getDisplayValue(formData[Object.keys(formData)[currentStep]], "").trim() === "") return;

    if (currentStep === 4) {
      saveCalculation();
      return;
    }

    gsap.to(formStepsRef.current[currentStep], {
      x: -50,
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        setCurrentStep((prev) => prev + 1);
        gsap.fromTo(
          formStepsRef.current[currentStep + 1],
          { x: 50, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
        );
      },
    });
  };

  const prevStep = () => {
    gsap.to(formStepsRef.current[currentStep], {
      x: 50,
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        setCurrentStep((prev) => prev - 1);
        gsap.fromTo(
          formStepsRef.current[currentStep - 1],
          { x: -50, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
        );
      },
    });
  };

  const resetForm = () => {
    gsap.to(formStepsRef.current[currentStep], {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        setCurrentStep(0);
        setFormData({
          country: "",
          programme: "",
          city: "",
          university: "",
          major: "",
        });
        gsap.fromTo(
          formStepsRef.current[0],
          { x: 0, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.3 }
        );
      },
    });
  };

  const steps = [
    {
      id: "country",
      label: "Country",
      type: "select",
      options: countries,
      isDisabled: false,
    },
    {
      id: "programme",
      label: "Programme",
      type: "select",
      options: [
        { value: "Masters", label: "Masters" },
        { value: "Bachelors", label: "Bachelors" },
      ],
      isDisabled: !formData.country,
    },
    {
      id: "city",
      label: "City",
      type: "select",
      options: cities,
      isDisabled: !formData.programme || !formData.country,
    },
    {
      id: "university",
      label: "University",
      type: "select",
      options: universities,
      isDisabled: !formData.city,
    },
    {
      id: "major",
      label: "Major",
      type: "select",
      options: majors,
      isDisabled: !formData.university,
    },
  ];

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "56px",
      fontSize: "1.125rem",
      borderColor: "#457b9d",
      "&:hover": {
        borderColor: "#1d3557",
      },
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused ? "#457b9d" : "white",
      color: isFocused ? "white" : "#1d3557",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1d3557",
    }),
  };

  return (
    <section
      ref={pinnedSectionRef}
      className="relative h-screen overflow-hidden bg-[#457b9d]"
      id="pinnedSection"
    >
      <div
        ref={bgOverlayRef}
        className="absolute inset-0 bg-[#457b9d] z-1 transition-colors duration-1000"
      ></div>

      <img
        ref={zoomImageRef}
        id="zoomImage"
        src="/Assets/diary.jpg"
        alt="Zoom Background"
        className="absolute w-full h-full object-cover scale-100 z-0 opacity-100"
      />

      <div className="relative z-10 px-0 md:px-52 flex items-center justify-center h-full">
        <div
          ref={calculatorRef}
          className="bg-white rounded-xl shadow-2xl p-10 w-full max-w-5xl  scale-100 opacity-0"
        >
          <div className="flex justify-center mb-10">
            <div id="calculator" className="flex space-x-3">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full transition-all duration-300 ${
                    index < currentStep
                      ? "bg-[#1d3557]"
                      : index === currentStep
                      ? "bg-[#457b9d]"
                      : "bg-gray-200"
                  }`}
                />
              ))}
            </div>
          </div>

          {currentStep < 5 ? (
            <form className="relative h-80 overflow-hidden">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  ref={(el) => (formStepsRef.current[index] = el)}
                  className={`absolute top-0 left-0 w-full ${
                    index !== currentStep ? "pointer-events-none" : ""
                  }`}
                  style={{ display: index === currentStep ? "block" : "none" }}
                >
                  <h2 className="text-3xl font-bold text-[#1d3557] text-center mb-8">
                    {step.label}
                  </h2>

                  {step.type === "select" ? (
                    <Select
                      id={step.id}
                      name={step.id}
                      options={step.options.map((opt) => ({
                        value:
                          typeof opt === "string"
                            ? opt
                            : getDisplayValue(opt.value ?? opt, ""),
                        label:
                          typeof opt === "string"
                            ? opt
                            : getDisplayValue(opt.label ?? opt, ""),
                      }))}
                      value={
                        formData[step.id]
                          ? {
                              value: getDisplayValue(formData[step.id], ""),
                              label: getDisplayValue(formData[step.id], ""),
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        handleChange(step.id, selectedOption)
                      }
                      isDisabled={step.isDisabled}
                      className="mb-10"
                      styles={customSelectStyles}
                      placeholder={`Select ${step.label}`}
                    />
                  ) : (
                    <input
                      type={step.type}
                      id={step.id}
                      value={formData[step.id]}
                      onChange={(e) =>
                        handleChange(step.id, { value: e.target.value })
                      }
                      className="w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#457b9d] focus:border-[#457b9d] transition mb-10"
                      required
                      autoFocus
                    />
                  )}

                  <div className="flex justify-between items-center">
                    {currentStep > 0 && (
                      <button
                        type="button"
                        onClick={prevStep}
                        className="flex relative items-center text-[#457b9d] hover:text-[#1d3557] transition-colors text-lg"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-7 w-7 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        Previous
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!formData[step.id]}
                      className={`ml-auto px-8 py-3 text-lg rounded-lg font-medium transition-all ${
                        formData[step.id]
                          ? "bg-[#457b9d] hover:bg-[#1d3557] text-white"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {currentStep === 4 ? "Calculate" : "Next"}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 inline-block ml-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>

                      {/* {!isLoading && (
                        <img
                          src={Spinner}
                          alt=""
                          className="size-6 absolute top-2 right-5"
                        />
                      )} */}
                    </button>
                  </div>
                </div>
              ))}
            </form>
          ) : (
            <div className="text-center">
              <div className=" bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] p-6 rounded-xl border border-gray-100">
                <div className="mb-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mx-auto text-[#457b9d]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h2 className="text-xl font-bold text-[#1d3557] mt-2">
                    Fee Calculated
                  </h2>
                  <p className="text-gray-600 mt-0 mb-3 text-lg">
                    Here's your submitted information
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-10 text-base">
                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">Country:</span>
                    <span className="font-semibold text-[#1d3557]">
                      {getDisplayValue(calculatedData?.country)}
                    </span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">Program:</span>
                    <span className="font-semibold text-[#1d3557]">
                      {getDisplayValue(calculatedData?.programme)}
                    </span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">City:</span>
                    <span className="font-semibold text-[#1d3557]">
                      {getDisplayValue(calculatedData?.city)}
                    </span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">Major:</span>
                    <span className="font-semibold text-[#1d3557]">
                      {getDisplayValue(calculatedData?.major)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-2">
                      <div className="flex justify-between border-b border-gray-200 pb-3">
                        <span className="font-medium text-gray-600">
                          University:
                        </span>
                        <span className="font-semibold text-[#1d3557]">
                          {getDisplayValue(calculatedData?.university)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">Est. Fee:</span>
                    <span className="font-semibold text-[#1d3557]">
                      {"PKR " + calculatedData?.totalFeeInPKR || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">
                      Min Est. Fee:
                    </span>
                    <span className="font-semibold text-[#1d3557]">
                      {"PKR " + calculatedData?.lowestEstimateFee || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-600">
                      Max Est. Fee:
                    </span>
                    <span className="font-semibold text-[#1d3557]">
                      {"PKR " + calculatedData?.highestEstimateFee || "N/A"}
                    </span>
                  </div>
                </div>

                <button
                  onClick={resetForm}
                  className="w-full bg-white text-[#457b9d] py-2 px-4 rounded-lg font-medium border-2 border-[#457b9d] hover:bg-[#f8fafc] hover:border-[#1d3557] hover:text-[#1d3557] transition-all text-lg"
                >
                  Start New Calculation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PinnedSection;
